import React from 'react';
import { LifeBuoy, MessageSquare, ChevronRight } from 'lucide-react';
import { SUPPORT_TOPICS } from '../utils/supportIntent';
import { openSupport } from '../../Layout/shell/shellEvents';
import styles from '../Chat.module.css';

/**
 * "Trying to reach Lodgeic?" — shown when a Team Chat search looks like a
 * support request (see utils/supportIntent). The button opens the Intercom
 * messenger; Lodgeic staff are never reachable in Team Chat itself.
 *
 *   layout="wide"    new-message dialog (icon beside the text, email fallback)
 *   layout="stacked" conversation list (narrow column)
 */
export default function SupportPrompt({ match, layout = 'stacked', onOpened }) {
  if (!match) return null;

  const open = () => {
    openSupport();
    onOpened?.();
  };

  if (match.strength === 'soft') {
    return (
      <button type="button" className={styles.supportSoft} onClick={open}>
        <LifeBuoy size={15} aria-hidden="true" />
        <span style={{ flex: '1 1 auto' }}>
          Question about using Lodgeic? <strong>Ask support</strong>
        </span>
        <ChevronRight size={14} aria-hidden="true" />
      </button>
    );
  }

  const topic = SUPPORT_TOPICS[match.topic] || SUPPORT_TOPICS.support;
  const icon = (
    <span className={styles.supportIcon} aria-hidden="true">
      <LifeBuoy size={layout === 'wide' ? 19 : 16} />
    </span>
  );
  const action = (
    <button type="button" className={`${styles.btnPrimary} ${layout === 'wide' ? '' : styles.btnSm}`} onClick={open}>
      <MessageSquare size={layout === 'wide' ? 15 : 14} aria-hidden="true" />
      {layout === 'wide' ? 'Chat with Lodgeic support' : 'Open support chat'}
    </button>
  );

  if (layout === 'wide') {
    return (
      <div role="status" className={styles.supportCard}>
        {icon}
        <div className={styles.supportText}>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <span className={styles.supportTitle}>{topic.title}</span>
            <span className={styles.supportBody}>{topic.body}</span>
          </div>
          <div className={styles.supportActions}>
            {action}
            <span className={styles.supportAlt}>
              or email <a href="mailto:support@lodgeic.io">support@lodgeic.io</a>
            </span>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div role="status" className={`${styles.supportCard} ${styles.stacked}`}>
      <div className={styles.supportCardHead}>
        {icon}
        <span className={styles.supportTitle}>{topic.title}</span>
      </div>
      <span className={styles.supportBody}>{topic.body}</span>
      <div>{action}</div>
    </div>
  );
}
